import { useState, useEffect, useCallback } from 'react';
import {
  AlertTriangle,
  Loader2,
  AlertCircle,
  Play,
  CheckCircle2,
  XCircle,
  Eye,
  X,
} from 'lucide-react';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  CartesianGrid,
  Legend,
  Cell,
} from 'recharts';
import clsx from 'clsx';
import { periodDisplayName, type Period, type Anomaly, type AnomalySummary } from '@/types';
import { anomalyApi, periodsApi } from '@/api/client';
import { useAppStore } from '@/store';

// --- Helpers -----------------------------------------------------------------

function formatAmount(amount: number | null): string {
  if (amount === null || amount === undefined) return '\u2014';
  if (amount === 0) return '\u2014';
  const abs = Math.abs(amount);
  const formatted = new Intl.NumberFormat('en-US', {
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(abs);
  return amount < 0 ? `(${formatted})` : formatted;
}

const severityConfig: Record<string, { label: string; bg: string; text: string; border: string }> = {
  high: { label: 'High', bg: 'bg-red-50', text: 'text-red-700', border: 'border-red-200' },
  medium: { label: 'Medium', bg: 'bg-amber-50', text: 'text-amber-700', border: 'border-amber-200' },
  low: { label: 'Low', bg: 'bg-blue-50', text: 'text-blue-700', border: 'border-blue-200' },
};

const categoryConfig: Record<string, { label: string; bg: string; text: string }> = {
  statistical: { label: 'Statistical', bg: 'bg-purple-50', text: 'text-purple-700' },
  variance: { label: 'Variance', bg: 'bg-orange-50', text: 'text-orange-700' },
  benford: { label: "Benford's Law", bg: 'bg-indigo-50', text: 'text-indigo-700' },
  ratio: { label: 'Ratio', bg: 'bg-teal-50', text: 'text-teal-700' },
  journal: { label: 'Journal Entry', bg: 'bg-pink-50', text: 'text-pink-700' },
};

const statusConfig: Record<string, { label: string; bg: string; text: string; icon: React.ElementType }> = {
  open: { label: 'Open', bg: 'bg-red-50', text: 'text-red-700', icon: AlertCircle },
  acknowledged: { label: 'Acknowledged', bg: 'bg-amber-50', text: 'text-amber-700', icon: Eye },
  false_positive: { label: 'False Positive', bg: 'bg-gray-100', text: 'text-gray-600', icon: XCircle },
  resolved: { label: 'Resolved', bg: 'bg-green-50', text: 'text-green-700', icon: CheckCircle2 },
};

// --- Resolve Modal -----------------------------------------------------------

interface ResolveModalProps {
  anomaly: Anomaly;
  onClose: () => void;
  onResolve: (id: number, status: string, notes: string) => Promise<void>;
}

function ResolveModal({ anomaly, onClose, onResolve }: ResolveModalProps) {
  const [status, setStatus] = useState(anomaly.resolution_status);
  const [notes, setNotes] = useState(anomaly.resolution_notes || '');
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async () => {
    setSubmitting(true);
    try {
      await onResolve(anomaly.id, status, notes);
      onClose();
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-lg mx-4 overflow-hidden">
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
          <h3 className="text-base font-semibold text-gray-900">Resolve Anomaly</h3>
          <button onClick={onClose} className="p-1 rounded-lg hover:bg-gray-100">
            <X className="w-4 h-4 text-gray-400" />
          </button>
        </div>
        <div className="p-6 space-y-4">
          <div>
            <p className="text-sm text-gray-600 mb-1">{anomaly.description}</p>
            <p className="text-xs text-gray-400">
              {anomaly.entity_name} / {anomaly.account_name}
            </p>
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-500 uppercase mb-1">Resolution Status</label>
            <select
              className="select-field text-sm w-full"
              value={status}
              onChange={(e) => setStatus(e.target.value as Anomaly['resolution_status'])}
            >
              <option value="open">Open</option>
              <option value="acknowledged">Acknowledged</option>
              <option value="false_positive">False Positive</option>
              <option value="resolved">Resolved</option>
            </select>
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-500 uppercase mb-1">Resolution Notes</label>
            <textarea
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-primary-500 focus:border-primary-500 resize-none"
              rows={4}
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Add notes about the resolution..."
            />
          </div>
        </div>
        <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-gray-200 bg-gray-50">
          <button onClick={onClose} className="btn-secondary text-sm">
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            disabled={submitting}
            className="btn-primary text-sm"
          >
            {submitting && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
            Save Resolution
          </button>
        </div>
      </div>
    </div>
  );
}

// --- Main Page ---------------------------------------------------------------

export default function AnomalyDashboard() {
  const { addNotification } = useAppStore();

  const [isLoading, setIsLoading] = useState(true);
  const [detecting, setDetecting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Period
  const [periods, setPeriods] = useState<Period[]>([]);
  const [selectedPeriod, setSelectedPeriod] = useState('');

  // Data
  const [anomalies, setAnomalies] = useState<Anomaly[]>([]);
  const [summary, setSummary] = useState<AnomalySummary | null>(null);

  // Filters
  const [severityFilter, setSeverityFilter] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('');
  const [entityFilter, setEntityFilter] = useState('');

  // Modal
  const [resolveAnomaly, setResolveAnomaly] = useState<Anomaly | null>(null);

  // Load periods
  useEffect(() => {
    const loadPeriods = async () => {
      try {
        const res = await periodsApi.list();
        setPeriods(res);
        const openPeriod = res.find((p) => p.status === 'open') || res[0];
        if (openPeriod) setSelectedPeriod(openPeriod.id);
      } catch {
        // silently fail
      }
    };
    loadPeriods();
  }, []);

  // Load anomaly results
  const loadResults = useCallback(async () => {
    if (!selectedPeriod) {
      setIsLoading(false);
      return;
    }
    setIsLoading(true);
    setError(null);
    try {
      const params: Record<string, any> = {};
      if (severityFilter) params.severity = severityFilter;
      if (categoryFilter) params.category = categoryFilter;

      const [anomaliesRes, summaryRes] = await Promise.all([
        anomalyApi.results(Number(selectedPeriod), params).catch(() => []),
        anomalyApi.summary(Number(selectedPeriod)).catch(() => null),
      ]);
      setAnomalies(anomaliesRes);
      setSummary(summaryRes);
    } catch (err: unknown) {
      const apiErr = err as { detail?: string; message?: string };
      setError(apiErr.detail || apiErr.message || 'Failed to load anomaly data');
    } finally {
      setIsLoading(false);
    }
  }, [selectedPeriod, severityFilter, categoryFilter]);

  useEffect(() => {
    if (selectedPeriod) loadResults();
  }, [loadResults, selectedPeriod]);

  // Run detection
  const handleDetect = async () => {
    if (!selectedPeriod) return;
    setDetecting(true);
    try {
      await anomalyApi.detect({ period_id: Number(selectedPeriod) });
      await loadResults();
      addNotification({ type: 'success', title: 'Detection Complete', message: 'Anomaly detection has been completed.' });
    } catch (err: unknown) {
      const apiErr = err as { detail?: string; message?: string };
      addNotification({ type: 'error', title: 'Detection Failed', message: apiErr.detail || 'Anomaly detection failed.' });
    } finally {
      setDetecting(false);
    }
  };

  // Resolve anomaly
  const handleResolve = async (id: number, status: string, notes: string) => {
    try {
      await anomalyApi.resolve(id, { resolution_status: status, resolution_notes: notes });
      setAnomalies((prev) =>
        prev.map((a) =>
          a.id === id
            ? { ...a, resolution_status: status as Anomaly['resolution_status'], resolution_notes: notes }
            : a,
        ),
      );
      addNotification({ type: 'success', title: 'Anomaly Updated', message: 'Resolution has been saved.' });
    } catch (err: unknown) {
      const apiErr = err as { detail?: string; message?: string };
      addNotification({ type: 'error', title: 'Update Failed', message: apiErr.detail || 'Failed to update anomaly.' });
    }
  };

  // Filtered anomalies
  const filteredAnomalies = anomalies.filter((a) => {
    if (entityFilter && a.entity_name !== entityFilter) return false;
    return true;
  });

  // Sort by severity: high, medium, low
  const severityOrder = { high: 0, medium: 1, low: 2 };
  const sortedAnomalies = [...filteredAnomalies].sort(
    (a, b) => (severityOrder[a.severity] || 3) - (severityOrder[b.severity] || 3),
  );

  // Unique entities for filter
  const uniqueEntities = [...new Set(anomalies.map((a) => a.entity_name))].sort();

  // Build Benford chart data
  const benfordData = summary?.benford?.digits || [];
  const benfordSignificant = summary?.benford?.significant ?? false;

  // Build category bar chart
  const categoryBarData = summary
    ? Object.entries(summary.by_category).map(([cat, count]) => ({
        category: categoryConfig[cat]?.label || cat,
        count,
      }))
    : [];

  // Build entity heatmap data
  const entityHeatmapData: { entity: string; category: string; count: number }[] = [];
  if (anomalies.length > 0) {
    const entityMap: Record<string, Record<string, number>> = {};
    anomalies.forEach((a) => {
      if (!entityMap[a.entity_name]) entityMap[a.entity_name] = {};
      entityMap[a.entity_name][a.category] = (entityMap[a.entity_name][a.category] || 0) + 1;
    });
    Object.entries(entityMap).forEach(([entity, categories]) => {
      Object.entries(categories).forEach(([category, count]) => {
        entityHeatmapData.push({ entity, category, count });
      });
    });
  }

  // Unique categories and entities for heatmap
  const heatmapCategories = [...new Set(entityHeatmapData.map((d) => d.category))].sort();
  const heatmapEntities = [...new Set(entityHeatmapData.map((d) => d.entity))].sort();

  function getHeatmapCount(entity: string, category: string): number {
    return entityHeatmapData.find((d) => d.entity === entity && d.category === category)?.count || 0;
  }

  function heatmapColor(count: number): string {
    if (count === 0) return 'bg-white';
    if (count === 1) return 'bg-red-100';
    if (count <= 3) return 'bg-red-200';
    if (count <= 5) return 'bg-red-300';
    if (count <= 10) return 'bg-red-400 text-white';
    return 'bg-red-600 text-white';
  }

  // Skeleton loading
  if (isLoading && anomalies.length === 0 && !summary) {
    return (
      <div className="space-y-6">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-lg bg-amber-100 flex items-center justify-center">
            <AlertTriangle className="w-5 h-5 text-amber-700" />
          </div>
          <div>
            <div className="skeleton w-64 h-7 mb-1" />
            <div className="skeleton w-80 h-4" />
          </div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {Array.from({ length: 3 }).map((_, i) => (
            <div key={i} className="card p-5 animate-pulse">
              <div className="skeleton w-16 h-3 mb-3" />
              <div className="skeleton w-12 h-8" />
            </div>
          ))}
        </div>
        <div className="card p-6 animate-pulse">
          <div className="skeleton w-full h-48" />
        </div>
        <div className="card p-6 animate-pulse">
          <div className="space-y-3">
            {Array.from({ length: 6 }).map((_, i) => (
              <div key={i} className="skeleton w-full h-10" />
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-lg bg-amber-100 flex items-center justify-center">
            <AlertTriangle className="w-5 h-5 text-amber-700" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Anomaly Detection & Audit Intelligence</h1>
            <p className="text-sm text-gray-500 mt-0.5">Automated anomaly detection and risk analysis</p>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <select
            className="select-field text-sm w-40"
            value={selectedPeriod}
            onChange={(e) => setSelectedPeriod(e.target.value)}
          >
            {periods.length === 0 && <option value="">No periods</option>}
            {periods.map((p) => (
              <option key={p.id} value={p.id}>{periodDisplayName(p)}</option>
            ))}
          </select>
          <button
            onClick={handleDetect}
            disabled={detecting || !selectedPeriod}
            className="btn-primary"
          >
            {detecting ? (
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
            ) : (
              <Play className="w-4 h-4 mr-2" />
            )}
            Run Detection
          </button>
        </div>
      </div>

      {/* Error */}
      {error && (
        <div className="card p-4 border-red-200 bg-red-50">
          <div className="flex items-center gap-2 text-red-700 text-sm">
            <AlertCircle className="w-4 h-4" />
            {error}
            <button onClick={loadResults} className="ml-auto text-xs font-medium underline">Retry</button>
          </div>
        </div>
      )}

      {/* No data */}
      {!summary && anomalies.length === 0 && !error ? (
        <div className="card py-16 text-center">
          <AlertTriangle className="w-12 h-12 mx-auto text-gray-300 mb-3" />
          <h3 className="text-base font-medium text-gray-900">No anomaly data available</h3>
          <p className="text-sm text-gray-500 mt-1">
            Select a period and click "Run Detection" to scan for anomalies.
          </p>
        </div>
      ) : (
        <>
          {/* ═══════════ SEVERITY SUMMARY CARDS ═══════════ */}
          {summary && (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {(['high', 'medium', 'low'] as const).map((sev) => {
                const cfg = severityConfig[sev];
                const count = summary[sev] || 0;
                const isActive = severityFilter === sev;
                return (
                  <button
                    key={sev}
                    onClick={() => setSeverityFilter(isActive ? '' : sev)}
                    className={clsx(
                      'card p-5 text-left transition-all',
                      isActive && `ring-2 ${cfg.border}`,
                      'hover:shadow-card-hover',
                    )}
                  >
                    <p className={clsx('text-xs font-semibold uppercase tracking-wider', cfg.text)}>{cfg.label} Severity</p>
                    <p className={clsx('text-3xl font-bold mt-1 font-mono', cfg.text)}>{count}</p>
                    <p className="text-xs text-gray-400 mt-1">
                      {isActive ? 'Click to clear filter' : 'Click to filter'}
                    </p>
                  </button>
                );
              })}
            </div>
          )}

          {/* ═══════════ CHARTS ROW ═══════════ */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Category Breakdown */}
            {categoryBarData.length > 0 && (
              <div className="card p-6">
                <h2 className="text-base font-semibold text-gray-900 mb-4">Anomalies by Category</h2>
                <ResponsiveContainer width="100%" height={220}>
                  <BarChart data={categoryBarData} layout="vertical" barSize={20}>
                    <CartesianGrid strokeDasharray="3 3" horizontal={false} stroke="#f1f5f9" />
                    <XAxis type="number" axisLine={false} tickLine={false} tick={{ fontSize: 11, fill: '#64748b' }} />
                    <YAxis
                      type="category"
                      dataKey="category"
                      axisLine={false}
                      tickLine={false}
                      tick={{ fontSize: 12, fill: '#374151' }}
                      width={100}
                    />
                    <Tooltip
                      contentStyle={{
                        backgroundColor: '#fff',
                        border: '1px solid #e2e8f0',
                        borderRadius: '8px',
                        fontSize: '13px',
                      }}
                    />
                    <Bar dataKey="count" name="Count" fill="#f59e0b" radius={[0, 4, 4, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            )}

            {/* Benford's Law Analysis */}
            {benfordData.length > 0 && (
              <div className="card p-6">
                <div className="flex items-center justify-between mb-4">
                  <h2 className="text-base font-semibold text-gray-900">Benford&apos;s Law Analysis</h2>
                  {benfordSignificant ? (
                    <span className="badge bg-red-50 text-red-700 gap-1">
                      <XCircle className="w-3 h-3" />
                      Significant Deviation
                    </span>
                  ) : (
                    <span className="badge bg-green-50 text-green-700 gap-1">
                      <CheckCircle2 className="w-3 h-3" />
                      Pass
                    </span>
                  )}
                </div>
                <ResponsiveContainer width="100%" height={200}>
                  <BarChart data={benfordData} barGap={4}>
                    <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f1f5f9" />
                    <XAxis
                      dataKey="digit"
                      axisLine={false}
                      tickLine={false}
                      tick={{ fontSize: 12, fill: '#64748b' }}
                    />
                    <YAxis
                      axisLine={false}
                      tickLine={false}
                      tick={{ fontSize: 11, fill: '#64748b' }}
                      tickFormatter={(v: number) => `${(v * 100).toFixed(0)}%`}
                    />
                    <Tooltip
                      contentStyle={{
                        backgroundColor: '#fff',
                        border: '1px solid #e2e8f0',
                        borderRadius: '8px',
                        fontSize: '13px',
                      }}
                      formatter={(value: number, name: string) => [`${(value * 100).toFixed(1)}%`, name]}
                    />
                    <Legend />
                    <Bar dataKey="expected_pct" name="Expected" fill="#93c5fd" radius={[4, 4, 0, 0]} />
                    <Bar dataKey="observed_pct" name="Observed" radius={[4, 4, 0, 0]}>
                      {benfordData.map((entry, index) => {
                        const deviation = Math.abs(entry.observed_pct - entry.expected_pct);
                        return (
                          <Cell
                            key={`cell-${index}`}
                            fill={deviation > 0.03 ? '#ef4444' : '#1e40af'}
                          />
                        );
                      })}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
                {summary?.benford && (
                  <div className="flex items-center gap-4 mt-3 text-xs text-gray-500">
                    <span>Chi-squared: <span className="font-mono font-medium text-gray-700">{Number(summary.benford.chi_squared).toFixed(2)}</span></span>
                    <span>p-value: <span className="font-mono font-medium text-gray-700">{Number(summary.benford.p_value).toFixed(4)}</span></span>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* ═══════════ ANOMALY LIST ═══════════ */}
          <div className="card overflow-hidden">
            <div className="px-6 py-4 border-b border-gray-200 bg-gray-50/50 flex items-center justify-between flex-wrap gap-3">
              <h2 className="text-base font-semibold text-gray-900">Detected Anomalies</h2>
              <div className="flex items-center gap-2">
                <select
                  className="select-field text-xs w-32"
                  value={severityFilter}
                  onChange={(e) => setSeverityFilter(e.target.value)}
                >
                  <option value="">All Severity</option>
                  <option value="high">High</option>
                  <option value="medium">Medium</option>
                  <option value="low">Low</option>
                </select>
                <select
                  className="select-field text-xs w-36"
                  value={categoryFilter}
                  onChange={(e) => setCategoryFilter(e.target.value)}
                >
                  <option value="">All Categories</option>
                  <option value="statistical">Statistical</option>
                  <option value="variance">Variance</option>
                  <option value="benford">Benford&apos;s Law</option>
                  <option value="ratio">Ratio</option>
                  <option value="journal">Journal Entry</option>
                </select>
                <select
                  className="select-field text-xs w-40"
                  value={entityFilter}
                  onChange={(e) => setEntityFilter(e.target.value)}
                >
                  <option value="">All Entities</option>
                  {uniqueEntities.map((e) => (
                    <option key={e} value={e}>{e}</option>
                  ))}
                </select>
              </div>
            </div>

            {sortedAnomalies.length === 0 ? (
              <div className="py-12 text-center">
                <CheckCircle2 className="w-10 h-10 mx-auto text-green-300 mb-2" />
                <p className="text-sm text-gray-500">No anomalies found matching your filters.</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-gray-200">
                      <th className="table-header w-20">Severity</th>
                      <th className="table-header w-24">Category</th>
                      <th className="table-header">Entity</th>
                      <th className="table-header">Account</th>
                      <th className="table-header">Description</th>
                      <th className="table-header text-right w-24">Detected</th>
                      <th className="table-header text-right w-32">Expected Range</th>
                      <th className="table-header w-28">Status</th>
                      <th className="table-header w-20">Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {sortedAnomalies.map((anomaly) => {
                      const sevCfg = severityConfig[anomaly.severity];
                      const catCfg = categoryConfig[anomaly.category] || { label: anomaly.category, bg: 'bg-gray-100', text: 'text-gray-700' };
                      const stsCfg = statusConfig[anomaly.resolution_status] || statusConfig.open;
                      const StsIcon = stsCfg.icon;

                      return (
                        <tr
                          key={anomaly.id}
                          className="border-b border-gray-100 hover:bg-gray-50/50 transition-colors"
                        >
                          <td className="table-cell">
                            <span className={clsx('badge', sevCfg.bg, sevCfg.text)}>{sevCfg.label}</span>
                          </td>
                          <td className="table-cell">
                            <span className={clsx('badge text-xs', catCfg.bg, catCfg.text)}>{catCfg.label}</span>
                          </td>
                          <td className="table-cell text-sm text-gray-700">{anomaly.entity_name}</td>
                          <td className="table-cell text-sm text-gray-600">{anomaly.account_name}</td>
                          <td className="table-cell text-sm text-gray-700 max-w-xs truncate" title={anomaly.description}>
                            {anomaly.description}
                          </td>
                          <td className="table-cell text-right font-mono text-sm text-gray-700">
                            {formatAmount(anomaly.detected_value)}
                          </td>
                          <td className="table-cell text-right font-mono text-xs text-gray-500">
                            {anomaly.expected_min !== null && anomaly.expected_max !== null
                              ? `${formatAmount(anomaly.expected_min)} - ${formatAmount(anomaly.expected_max)}`
                              : '\u2014'}
                          </td>
                          <td className="table-cell">
                            <span className={clsx('badge gap-1', stsCfg.bg, stsCfg.text)}>
                              <StsIcon className="w-3 h-3" />
                              {stsCfg.label}
                            </span>
                          </td>
                          <td className="table-cell">
                            <button
                              onClick={() => setResolveAnomaly(anomaly)}
                              className="text-xs font-medium text-primary-600 hover:text-primary-800 underline"
                            >
                              Resolve
                            </button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          {/* ═══════════ ENTITY RISK HEATMAP ═══════════ */}
          {heatmapEntities.length > 0 && heatmapCategories.length > 0 && (
            <div className="card p-6">
              <h2 className="text-base font-semibold text-gray-900 mb-4">Entity Risk Heatmap</h2>
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr>
                      <th className="table-header">Entity</th>
                      {heatmapCategories.map((cat) => (
                        <th key={cat} className="table-header text-center w-24">
                          {categoryConfig[cat]?.label || cat}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {heatmapEntities.map((entity) => (
                      <tr key={entity} className="border-b border-gray-100">
                        <td className="table-cell text-sm font-medium text-gray-700">{entity}</td>
                        {heatmapCategories.map((cat) => {
                          const count = getHeatmapCount(entity, cat);
                          return (
                            <td key={cat} className="table-cell text-center">
                              <span
                                className={clsx(
                                  'inline-flex items-center justify-center w-8 h-8 rounded text-xs font-mono font-bold',
                                  heatmapColor(count),
                                  count === 0 && 'border border-gray-200 text-gray-300',
                                )}
                              >
                                {count}
                              </span>
                            </td>
                          );
                        })}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <div className="flex items-center gap-3 mt-3 text-xs text-gray-500">
                <span>Intensity:</span>
                <span className="inline-flex items-center gap-1"><span className="w-4 h-4 rounded bg-white border border-gray-200"></span> 0</span>
                <span className="inline-flex items-center gap-1"><span className="w-4 h-4 rounded bg-red-100"></span> 1</span>
                <span className="inline-flex items-center gap-1"><span className="w-4 h-4 rounded bg-red-200"></span> 2-3</span>
                <span className="inline-flex items-center gap-1"><span className="w-4 h-4 rounded bg-red-300"></span> 4-5</span>
                <span className="inline-flex items-center gap-1"><span className="w-4 h-4 rounded bg-red-400"></span> 6-10</span>
                <span className="inline-flex items-center gap-1"><span className="w-4 h-4 rounded bg-red-600"></span> 10+</span>
              </div>
            </div>
          )}
        </>
      )}

      {/* Resolve Modal */}
      {resolveAnomaly && (
        <ResolveModal
          anomaly={resolveAnomaly}
          onClose={() => setResolveAnomaly(null)}
          onResolve={handleResolve}
        />
      )}
    </div>
  );
}
